import { DEFAULT_BANNER_SIZE } from "./types";

/** Minimal asset shape needed to choose a banner group's thumbnail. */
type ThumbnailAssetLike = {
  type: string;
  width: number;
  height: number;
  language?: string;
};

/** Anything with pixel dimensions we can compare for squareness. */
type SizeLike = { width: number; height: number };

/** Aspect-ratio distance from a perfect square (0 = square). */
const squareDistance = (a: SizeLike) => Math.abs(a.width / a.height - 1);

/**
 * Returns the candidate whose aspect ratio is closest to square (width/height
 * ≈ 1). Candidates with a non-positive width/height are skipped to avoid
 * divide-by-zero / NaN. Ties keep the earliest candidate (stable).
 */
export const closestToSquare = <T extends SizeLike>(
  candidates: readonly T[],
): T | undefined =>
  candidates.reduce<T | undefined>((best, a) => {
    if (a.width <= 0 || a.height <= 0) return best;
    if (!best) return a;
    return squareDistance(a) < squareDistance(best) ? a : best;
  }, undefined);

/**
 * Picks the asset to use as a banner group's thumbnail.
 *
 * Preference order:
 *  1. an English (`en`) asset at the default 300×250 size,
 *  2. an image banner at the default 300×250 size,
 *  3. any asset at the default 300×250 size,
 *  4. the image banner closest to square (width/height ≈ 1),
 *  5. the asset (any type) closest to square,
 *  6. the first asset as a last resort.
 *
 * Preferring the English 300×250 keeps the thumbnail consistent across groups;
 * the square fallback keeps it sharp and fills the tile when 300×250 isn't
 * available.
 */
export function selectThumbnailAsset<T extends ThumbnailAssetLike>(
  assets: readonly T[],
): T | undefined {
  const isPreferredSize = (a: T) =>
    a.width === DEFAULT_BANNER_SIZE.width &&
    a.height === DEFAULT_BANNER_SIZE.height;
  const isEnglish = (a: T) => a.language?.toLowerCase() === "en";

  const images = assets.filter((a) => a.type === "image_banner");

  return (
    assets.find((a) => isPreferredSize(a) && isEnglish(a)) ??
    images.find(isPreferredSize) ??
    assets.find(isPreferredSize) ??
    closestToSquare(images) ??
    closestToSquare(assets) ??
    assets[0]
  );
}
